import {
  JAIN_MONTH_OPTIONS,
  JAIN_TITHI_OPTIONS,
  PAKSHA_OPTIONS,
} from "@project/types/jain-panchang";

export function labelForOption(
  options: readonly { value: string; label: string }[],
  value?: string | null
) {
  if (!value) {
    return "";
  }
  return options.find(o => o.value === value)?.label ?? capitalize(value);
}

export function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatPanchangDateKey(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatJainMonth(value?: string | null) {
  return labelForOption(JAIN_MONTH_OPTIONS, value);
}

export function formatPaksha(value?: string | null) {
  return labelForOption(PAKSHA_OPTIONS, value);
}

export function formatJainTithi(value?: string | null) {
  return labelForOption(JAIN_TITHI_OPTIONS, value);
}

export function formatPakshaTithiLine(paksha?: string | null, tithi?: string | null) {
  const pakshaLabel = formatPaksha(paksha);
  const tithiLabel = formatJainTithi(tithi);
  if (!pakshaLabel && !tithiLabel) {
    return "";
  }
  return [pakshaLabel, tithiLabel].filter(Boolean).join(" ");
}

export function formatPakshaFortnight(paksha?: string | null) {
  const label = formatPaksha(paksha);
  if (!label) {
    return "";
  }
  if (paksha === "sud") {
    return `${label}`;
  }
  if (paksha === "vad") {
    return `${label}`;
  }
  return label;
}

export function formatTithiBadge(tithi?: string | null) {
  const label = formatJainTithi(tithi);
  if (!label) {
    return "";
  }
  return label;
}

export function formatPanchangHeadingDate(entry: {
  english_date: string;
  day_of_week?: string | null;
}) {
  const date = new Date(`${entry.english_date}T12:00:00`);
  const formatted = date.toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  if (entry.day_of_week) {
    return `${entry.day_of_week}, ${formatted}`;
  }
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatShortDisplayDate(dateStr: string) {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatUpcomingCardDate(entry: {
  english_date: string;
  day_of_week?: string | null;
}) {
  const date = new Date(`${entry.english_date}T12:00:00`);
  const weekday = entry.day_of_week
    ? entry.day_of_week.slice(0, 3).toUpperCase()
    : date
        .toLocaleDateString("en-IN", { weekday: "short" })
        .slice(0, 3)
        .toUpperCase();
  const monthDay = date
    .toLocaleDateString("en-IN", { month: "short", day: "numeric" })
    .toUpperCase()
    .replace(",", "");

  return `${weekday}, ${monthDay}`;
}

export function formatUpcomingCardTitle(entry: {
  jain_month: string;
  jain_tithi: string;
  festival_name?: string | null;
  religious_occasion?: string | null;
}) {
  if (entry.festival_name) {
    return entry.festival_name;
  }
  if (entry.religious_occasion) {
    return entry.religious_occasion;
  }
  const month = formatJainMonth(entry.jain_month);
  const tithi = formatJainTithi(entry.jain_tithi);
  return [month, tithi].filter(Boolean).join(" ");
}

/** Human-readable tithi string for sponsorship `preferred_tithi`. */
export function formatPreferredTithiOption(entry: {
  jain_month: string;
  paksha: string;
  jain_tithi: string;
  type?: string | null;
  festival_name?: string | null;
  religious_occasion?: string | null;
}) {
  const base = [
    formatJainMonth(entry.jain_month),
    formatPaksha(entry.paksha),
    formatJainTithi(entry.jain_tithi),
  ]
    .filter(Boolean)
    .join(" ");

  const extras: string[] = [];
  if (entry.festival_name?.trim()) {
    extras.push(entry.festival_name.trim());
  } else if (entry.religious_occasion?.trim()) {
    extras.push(entry.religious_occasion.trim());
  }
  if (entry.type?.trim()) {
    extras.push(capitalize(entry.type.trim()));
  }

  if (!base && extras.length === 0) {
    return "";
  }
  if (!extras.length) {
    return base;
  }
  if (!base) {
    return extras.join(" · ");
  }
  return `${base} · ${extras.join(" · ")}`;
}
